/** @jsxImportSource @emotion/react */

import { FC, useMemo, useState } from 'react'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import { useMutation } from '@apollo/client'
import { useParams } from 'react-router-dom'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { findStudySchedule_findStudySchedule as StudySchedule } from '__generatedTypes__/findStudySchedule'
import { UPDATE_STUDY_SCHEDULE } from 'queries/studySchedule'
import { updateStudySchedule, updateStudyScheduleVariables } from '__generatedTypes__/updateStudySchedule'
import { BaseModal } from 'components/Modals/BaseModal'
import { toast } from 'utils/toast'
import { PlatformSlug } from 'constants/consts'
import { SectionItem } from '../SuccessStories/SectionItem'
import * as classes from '../SuccessStories/styles'
import { StudyGuideScheduleForm, FormValues, SINGLE_LINK_PLATFORMS } from './StudyGuideScheduleForm'

type Props = {
  studySchedule?: StudySchedule | null
}

export const StudyGuideSchedule: FC<Props> = ({ studySchedule }) => {
  const { platform } = useParams()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [updateStudySchedule] = useMutation<updateStudySchedule, updateStudyScheduleVariables>(UPDATE_STUDY_SCHEDULE)

  const [orderIds, items] = useMemo(() => {
    if (!studySchedule) {
      return [[], []]
    }

    const isSingleWeek = SINGLE_LINK_PLATFORMS.includes(platform as PlatformSlug)

    const items = [
      {
        id: 'week8url',
        label: studySchedule.week8url,
      },
      {
        id: 'week10url',
        label: studySchedule.week10url,
      },
      {
        id: 'week12url',
        label: studySchedule.week12url,
      },
    ]
      .slice(0, isSingleWeek ? 1 : 3)
      .filter(({ label }) => !!label)

    const orderIds = items.map(({ id }) => id)

    return [orderIds, items]
  }, [studySchedule, platform])

  const initialValues = useMemo(() => {
    if (!studySchedule) {
      return {}
    }

    const {
      description,
      week8url,
      week8settingsTitle,
      week8settingsDescription,
      week10url,
      week10settingsTitle,
      week10settingsDescription,
      week12url,
      week12settingsTitle,
      week12settingsDescription,
    } = studySchedule
    return {
      description,
      week8url,
      week8settingsTitle,
      week8settingsDescription,
      week10url,
      week10settingsTitle,
      week10settingsDescription,
      week12url,
      week12settingsTitle,
      week12settingsDescription,
    }
  }, [studySchedule])

  const handleEdtClick = () => setIsModalOpen(true)
  const handleClose = () => setIsModalOpen(false)

  const handleSubmit = async (values: FormValues) => {
    try {
      const { title, id } = studySchedule || {}

      await updateStudySchedule({
        variables: {
          updateStudyScheduleInputFieldName: {
            title,
            id,
            ...values,
          },
        },
      })

      handleClose()

      toast.success('Study schedule updated successfully')
    } catch (error) {
      toast.error('Failed to update study schedule')
    }
  }

  if (!studySchedule) {
    return null
  }

  return (
    <>
      <li css={classes.listItem}>
        <div>
          <Typography variant="subtitle2" css={classes.title}>
            {studySchedule.title}
          </Typography>
          <SectionItem orderIds={orderIds} items={items} />
        </div>
        <IconButton css={classes.editButton} onClick={handleEdtClick}>
          <EditIcon />
        </IconButton>
      </li>
      {isModalOpen && (
        <BaseModal open onClose={handleClose} css={classes.modal}>
          <StudyGuideScheduleForm initialValues={initialValues} onSubmit={handleSubmit} onClose={handleClose} />
        </BaseModal>
      )}
    </>
  )
}
